import { toBlob } from 'html-to-image';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db, storage } from './firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Capture a screenshot of the current page using html-to-image
 * @returns {Promise<Blob|null>} - Screenshot as a Blob, or null if capture fails
 */
export async function captureScreenshot() {
  try {
    const blob = await toBlob(document.body, {
      cacheBust: true,
      skipFonts: true,
      filter: (node) => {
        return !node.classList?.contains('feedback-ignore');
      },
    });
    return blob;
  } catch (error) {
    console.error('Error capturing screenshot:', error);
    return null;
  }
}

/**
 * Upload a feedback screenshot to Firebase Storage
 * @param {Blob} blob - The screenshot blob
 * @returns {Promise<string>} - The download URL
 */
export async function uploadFeedbackScreenshot(blob) {
  if (!blob) {
    throw new Error('Ingen skjermbilete å laste opp');
  }

  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);
  const imagePath = `feedback-screenshots/${timestamp}-${randomId}.png`;
  const imageRef = ref(storage, imagePath);

  await uploadBytes(imageRef, blob);
  const downloadURL = await getDownloadURL(imageRef);

  return downloadURL;
}

/**
 * Submit feedback via Firebase Cloud Function
 * @param {Object} feedbackData - The feedback data
 * @param {string} feedbackData.type - 'bug' | 'forslag' | 'sporsmal'
 * @param {string} feedbackData.title - Feedback title
 * @param {string} feedbackData.description - Feedback description
 * @param {string|null} feedbackData.screenshotUrl - Screenshot URL (optional)
 * @param {string} feedbackData.pageUrl - Current page URL
 * @param {string} feedbackData.timestamp - ISO timestamp
 * @param {string} feedbackData.userAgent - Browser user agent
 * @returns {Promise<{ success: boolean, issueUrl?: string, error?: string }>}
 */
export async function submitFeedback(feedbackData) {
  try {
    const functions = getFunctions();
    const createFeedback = httpsCallable(functions, 'createFeedback');

    const result = await createFeedback(feedbackData);

    return {
      success: true,
      issueUrl: result.data.issueUrl,
    };
  } catch (error) {
    console.error('Error submitting feedback:', error);

    if (error.code === 'functions/not-found') {
      return {
        success: false,
        error: 'Feedback-funksjonen er ikkje sett opp enno. Kontakt admin.',
      };
    }

    return {
      success: false,
      error: error.message || 'Kunne ikkje sende tilbakemelding',
    };
  }
}

/**
 * Fetch all feedback items from Firestore, sorted by newest first
 * @returns {Promise<Array>} - Array of feedback objects
 */
export async function getAllFeedback() {
  try {
    const q = query(
      collection(db, 'feedback'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching feedback:', error);
    throw error;
  }
}
